import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, Trash2, CheckCircle2, Loader2, Plus, FileSpreadsheet } from 'lucide-react';

const MultiFileUpload = ({ files, onAddFile, onRemoveFile, loading }) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files) {
      for (const file of Array.from(e.dataTransfer.files)) {
        await handleFile(file);
      }
    }
  };

  const handleFileInput = async (e) => {
    if (e.target.files) {
      for (const file of Array.from(e.target.files)) {
        await handleFile(file);
      }
      e.target.value = '';
    }
  };

  const handleFile = async (file) => {
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    if (!validExtensions.includes(fileExtension)) {
      alert('Por favor, selecione um arquivo Excel (.xlsx, .xls) ou CSV (.csv)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('O arquivo é muito grande. Tamanho máximo: 10MB');
      return;
    }
    try {
      await onAddFile(file);
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
    }
  };

  const totalSales = files.reduce((sum, f) => sum + f.count, 0);

  return (
    <div className="space-y-4">
      {/* Área de drag & drop */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive
            ? 'border-purple-400 bg-purple-400/10'
            : files.length > 0
            ? 'border-purple-500/50 bg-purple-500/5'
            : 'border-white/20 hover:border-white/40'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFileInput}
          multiple
          className="hidden"
        />

        {loading ? (
          <div className="space-y-2">
            <Loader2 className="w-10 h-10 text-blue-400 mx-auto animate-spin" />
            <p className="text-blue-400 font-semibold">Processando arquivo...</p>
            <p className="text-white/70 text-sm">Enviando ao micro-serviço</p>
          </div>
        ) : (
          <div className="space-y-3">
            <Upload className="w-10 h-10 text-white/40 mx-auto" />
            <div>
              <p className="text-white font-semibold mb-1">
                {files.length === 0
                  ? 'Arraste os extratos das maquininhas aqui'
                  : 'Arraste mais arquivos para adicionar'}
              </p>
              <p className="text-white/60 text-sm mb-3">ou</p>
              <Button
                onClick={() => fileInputRef.current?.click()}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                {files.length === 0 ? 'Selecionar Arquivo(s)' : 'Adicionar Arquivo'}
              </Button>
            </div>
            <p className="text-white/50 text-xs">
              Formatos aceitos: .xlsx, .xls, .csv (máx. 10MB cada)
            </p>
          </div>
        )}
      </div>

      {/* Lista de arquivos carregados */}
      {files.length > 0 && (
        <div className="space-y-2">
          <p className="text-white/60 text-xs uppercase tracking-wider mb-1">
            {files.length} {files.length === 1 ? 'arquivo carregado' : 'arquivos carregados'} — {totalSales} vendas no total
          </p>
          {files.map((fileInfo, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10 hover:border-white/20 transition-colors"
            >
              <div className="flex items-center gap-3">
                <FileSpreadsheet className="w-5 h-5 text-purple-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">{fileInfo.fileName}</p>
                  <p className="text-white/50 text-xs">
                    {fileInfo.operatorName} — {fileInfo.count} {fileInfo.count === 1 ? 'venda' : 'vendas'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <CheckCircle2 className="w-4 h-4 text-green-400" />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemoveFile(index)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/20 h-8 w-8 p-0"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiFileUpload;
